import { HttpStatusCode } from '@angular/common/http';
import { Injectable, isDevMode, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { UserEnum, DeliveryModeType, Page, PageType } from '@boels/models';
import { Observable, combineLatest, concatMap, filter, map, of } from 'rxjs';
import { CartFacade } from '../../cart/facade/cart.facade';
import { CmsFacade } from '../../cms/facade/cms.facade';
import { CultureFacade } from '../../cultures/facade/cultures.facade';
import { RedirectService } from '../../redirect/redirect.service';

@Injectable({ providedIn: 'root' })
export class CheckoutGuard {
  private router = inject(Router);
  private cartFacade = inject(CartFacade);
  private cmsFacade = inject(CmsFacade);
  private redirectService = inject(RedirectService);
  private cultureFacade = inject(CultureFacade);

  canActivatecheckout(
    route: ActivatedRouteSnapshot,
  ): Observable<boolean | UrlTree> {
    return combineLatest([
      this.cartFacade.getActiveCart(true),
      this.cartFacade.getCartLoading(),
      this.cultureFacade.getActiveCulture(),
      this.cartFacade.getCheckoutAccount(),
    ]).pipe(
      filter((latest) => !latest[1]),
      map(([activeCart, , culture, checkoutAccount]) => {
        if (activeCart && activeCart.entries && activeCart.entries.length > 0) {
          if (
            checkoutAccount === UserEnum.ANONYMOUS &&
            activeCart.user?.name !== 'guest'
          )
            return this.router.createUrlTree([culture, 'checkout-account']);

          const checkoutStep = route.params['checkoutStep'];
          switch (checkoutStep) {
            case 'customer-details': {
              return true;
            }
            case 'delivery-mode': {
              if (activeCart.deliveryAddress) {
                return true;
              }
              return this.router.createUrlTree([
                'checkout',
                'customer-details',
              ]);
            }
            case 'shipping-address': {
              if (activeCart.deliveryMode?.code === DeliveryModeType.DELIVERY) {
                return true;
              }
              return this.router.createUrlTree(['checkout', 'delivery-mode']);
            }
            case 'pickup': {
              if (activeCart.deliveryMode?.code === DeliveryModeType.PICKUP) {
                return true;
              }
              return this.router.createUrlTree(['checkout', 'delivery-mode']);
            }
            case 'review-order': {
              if (activeCart.deliveryMode && activeCart.deliveryAddress) {
                return true;
              }
              if (activeCart.deliveryMode?.code === DeliveryModeType.PICKUP) {
                return this.router.createUrlTree(['checkout', 'pickup']);
              }
              if (activeCart.deliveryMode?.code === DeliveryModeType.DELIVERY) {
                return this.router.createUrlTree([
                  'checkout',
                  'shipping-address',
                ]);
              }
              return this.router.createUrlTree([culture]);
            }
            default: {
              if (isDevMode()) {
                console.warn('checkout-guard: not a checkout step');
              }
              return this.router.createUrlTree([culture]);
            }
          }
        }
        // no cart
        if (isDevMode()) {
          console.warn('checkout-guard: no cart', activeCart);
        }

        return this.router.createUrlTree([culture]);
      }),
    );
  }

  isCmsPage(
    activatedRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    const route: string = state.url;
    this.cmsFacade.loadPage(route, PageType.CONTENT_PAGE);

    return combineLatest([
      this.cmsFacade.activeLoaded$,
      this.cmsFacade.isCmsRoute$,
    ]).pipe(
      concatMap(([active, isCmsRoute]: [Page | undefined, boolean]) => {
        if (active?.foundByRedirect && active?.label) {
          return this.redirectService
            .redirect(active.label, HttpStatusCode.MovedPermanently)
            .pipe(map(() => false));
        }

        if (!isCmsRoute && !this.isSmartEditPage(route)) {
          this.redirectService.setStatusCode(HttpStatusCode.NotFound);
          return of(true);
        }
        return this.canActivatecheckout(activatedRoute);
      }),
    );
  }

  isSmartEditPage(route: string): boolean {
    return (route.split('/').pop() as string).includes('cx-preview');
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | UrlTree {
    return this.isCmsPage(route, state);
  }
}
